import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import type { KibCheckboxModelValue } from '@chewy/kib-choices-react'
import { Grid, type Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import { type User, Utils } from '@pbt/pbt-ui-components'
import {
  CheckCircle as CheckCircleIcon,
  Email,
  EmailOpened,
} from '@pbt/pbt-ui-components/src/icons'

import Avatar from '~/components/common/Avatar'
import QuickFilterButton from '~/components/common/buttons/QuickFilterButton'
import { ConversationStatus } from '~/constants/communications'
import FeatureToggle from '~/constants/featureToggle'
import { setConversationsListFilters } from '~/store/actions/conversations'
import { getCurrentUser } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getConversationsListFilters } from '~/store/reducers/conversations'
import type { TableFilter } from '~/types'

import CategoryButtonGroup from '../common/CategoryButtonGroup'
import ConversationCheckboxFilters from './ConversationCheckboxFilters'

const useStyles = makeStyles(
  (theme) => ({
    quickFilterButton: {
      margin: theme.spacing(0.5, 1, 0.5, 0),
    },
    categoryButtonGroup: {
      marginRight: theme.spacing(2),
    },
  }),
  { name: 'ConversationFilters' },
)

export interface HeaderFilter {
  active: boolean
  iconComponent?: React.JSXElementConstructor<any>
  iconProps?: { person: User }
  onSelected: (checked?: KibCheckboxModelValue) => void
  text: string
}

const ConversationFilters = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { clientId } = useParams()
  const currentUser = useSelector(getCurrentUser)
  const filters = useSelector(getConversationsListFilters)
  const isCVCSystemMessageSuppressionEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_SYSTEM_MESSAGE_SUPPRESSION),
  )
  const isSuppressAllSystemMessagesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SUPPRESS_ALL_SYSTEM_MESSAGES),
  )
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))
  const useShinkFilters = Boolean(clientId)
  const { t } = useTranslation(['Common', 'Communications'])

  const statusFilterList = ((filters.status?.value as string) || '')
    .split(',')
    .filter(R.length)

  const setFilters = (newFilters: Record<string, TableFilter>) => {
    dispatch(setConversationsListFilters(newFilters))
  }

  const toggleStatusFilter = (status: string) =>
    Utils.toggleListItem(status, statusFilterList).join(',')

  const applyFilter = (filter: string, value: TableFilter) => {
    setFilters({ ...filters, [filter]: value })
  }

  const statusFilters: HeaderFilter[] = !useShinkFilters
    ? [
        {
          active: R.includes(ConversationStatus.ARCHIVED, statusFilterList),
          iconComponent: CheckCircleIcon,
          text: t('Communications:STATUS.CONVERSATION_ARCHIVED'),
          onSelected: () => {
            setFilters({
              ...filters,
              status: {
                ...filters.status,
                value: toggleStatusFilter(ConversationStatus.ARCHIVED),
              },
            })
          },
        },
        {
          active: R.includes(ConversationStatus.UNREAD, statusFilterList),
          text: t('Communications:STATUS.CONVERSATION_UNREAD'),
          iconComponent: Email,
          onSelected: () => {
            setFilters({
              ...filters,
              status: {
                ...filters.status,
                value: toggleStatusFilter(ConversationStatus.UNREAD),
              },
            })
          },
        },
        {
          active: R.includes(ConversationStatus.OPEN, statusFilterList),
          text: t('Communications:STATUS.CONVERSATION_OPEN'),
          iconComponent: EmailOpened,
          onSelected: () => {
            setFilters({
              ...filters,
              status: {
                ...filters.status,
                value: toggleStatusFilter(ConversationStatus.OPEN),
              },
            })
          },
        },
      ]
    : []

  const excludeSystemMessagesFilter: HeaderFilter[] =
    isSuppressAllSystemMessagesEnabled
      ? [
          {
            active: !filters.excludeSystemMessages?.value,
            text: t('Common:SHOW_SYSTEM_MESSAGES'),
            onSelected: (checked: KibCheckboxModelValue) => {
              setFilters({
                ...filters,
                excludeSystemMessages: {
                  value: !checked,
                },
              })
            },
          },
        ]
      : []

  const headerFilters: HeaderFilter[] = [
    {
      active: filters.assignee?.value === currentUser.id,
      iconComponent: Avatar,
      iconProps: { person: currentUser },
      text: t('Common:ASSIGNED_TO_ME'),
      onSelected: () => {
        setFilters({
          ...filters,
          assignee: {
            ...filters.assignee,
            value:
              filters.assignee?.value === currentUser.id
                ? null
                : currentUser.id,
            humanReadable: Utils.getPersonString(currentUser),
          },
        })
      },
    },
    ...(isCVCSystemMessageSuppressionEnabled
      ? excludeSystemMessagesFilter
      : statusFilters),
  ]

  return (
    <Grid
      container
      item
      xs
      alignItems="center"
      direction={isMobile ? 'column' : 'row'}
    >
      <CategoryButtonGroup
        applyFilter={applyFilter}
        className={classes.categoryButtonGroup}
        filter={filters.category}
      />
      {isCVCSystemMessageSuppressionEnabled ? (
        <ConversationCheckboxFilters filters={headerFilters} />
      ) : (
        headerFilters.map((filter) => (
          <Grid key={filter.text}>
            <QuickFilterButton
              IconComponent={filter.iconComponent}
              IconProps={filter.iconProps}
              active={filter.active}
              className={classes.quickFilterButton}
              text={filter.text}
              onClick={filter.onSelected}
            />
          </Grid>
        ))
      )}
    </Grid>
  )
}

export default ConversationFilters
